import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import decodeToken from '../../../../utils/decode-token';
import { useTranslation } from 'react-i18next';
import './experience.scss';

import experienceImages from '../../../../assets/img/experiences';

import Wadsworth from '../../role-hac/wadsworth/wadsworth';
import ExperienceCatalogHero from './catalogHero';
import ExperienceCatalogItem from './catalog-item';

const {
  AlmasixsensesGallery,
  AlpineEscapeGallery,
  AndorraGallery,
  AntinoriGallery,
  AntoniewiczGallery,
  AntwerpGallery,
  CanMuloGallery,
  CaviarhouseGallery,
  ChampagneNewGallery,
  CostaSmeraldaGallery,
  cotedAzurGallery,
  CourchevelGallery,
  CourmayeurGallery,
  CulturalcuratorGallery,
  EastnorGallery,
  FerrettiGallery,
  GlobeairGallery,
  HahnenkammGallery,
  Hahnenkamm2026Gallery,
  HommageGallery,
  IbizaGallery,
  InsidetrackGallery,
  IslandEscapeMykonosGallery,
  MandarinGallery,
  MayrlifeGallery,
  MykonosGallery,
  Mykonos2025Gallery,
  PrivateParisGallery,
  Wimbledon2026Gallery,
  WimbledonGallery,
  AlmaGallery,
  ChampagneGallery,
  KitzbuhelGallery,
} = experienceImages;

const ADMIN_ROLES = ['admin', 'marketAdmin', 'countryAdmin'];
const PMA_ROLES = ['pma', 'pmaGM'];
const HAC_SALES_RETAILER_ROLES = ['hac', 'salesperson', 'retailerGM'];

const normalizeCode = (v) => String(v || '').trim().toUpperCase();

const toCountryList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(normalizeCode).filter(Boolean);

  // soporta "ES,FR" o "ES;FR" o "ES FR"
  return String(value)
    .split(/[,; ]+/)
    .map(normalizeCode)
    .filter(Boolean);
};

const ExperienceCatalog = () => {
  window.scroll(0, 0);
  const { t } = useTranslation();

  const token = sessionStorage.getItem('token') || null;

  const role = useMemo(() => {
    try {
      return token ? decodeToken(token).role : null;
    } catch {
      return null;
    }
  }, [token]);

  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('loggedUser') || '{}');
    } catch {
      return {};
    }
  }, []);

  // ✅ lista final de países según rol
  const countries = useMemo(() => {
    if (!role) return [];

    if (ADMIN_ROLES.includes(role)) {
      return toCountryList(loggedUser?.managed_area_codes);
    }

    if (PMA_ROLES.includes(role)) {
      return (loggedUser?.area_code_visibility || [])
        .map(x => normalizeCode(x?.area_code))
        .filter(Boolean);
    }

    if (HAC_SALES_RETAILER_ROLES.includes(role)) {
      const code = normalizeCode(loggedUser?.area_code);
      return code ? [code] : [];
    }

    // si cae en otros roles, no ve ninguna de estas experiencias por defecto
    return [];
  }, [role, loggedUser]);

  const canSeeAndorra = useMemo(() => countries.includes('ES'), [countries]);
  const canSeeCourchevel = useMemo(() => countries.includes('FR'), [countries]);

  const [linkRole, setLinkRole] = useState(null);

  useEffect(() => {
    switch (role) {
      case 'hac':
        setLinkRole('hac');
        break;
      case 'pma':
      case 'pmaGM':
        setLinkRole('pma');
        break;
      case 'retailer':
      case 'retailerGM':
        setLinkRole('retailer');
        break;
      case 'admin':
      case 'marketAdmin':
      case 'countryAdmin':
        setLinkRole('hac');
        break;
      default:
        setLinkRole(null);
    }
  }, [role]);

  const [activeTab, setActiveTab] = useState('new');
  const handleTabClick = (tab) => setActiveTab(tab);

  const pastExperiences = useMemo(() => {
    const items = [
      {
        event: 'alma',
        image: AlmaGallery,
        titleKey: 'hac-landing-gallery-title-alma',
        dateKey: '',
        textKey: 'hac-landing-gallery-text-alma',
      },
      {
        event: 'CostaSmeralda',
        image: CostaSmeraldaGallery,
        titleKey: 'hac-landing-gallery-title-CostaSmeralda',
        dateKey: '',
        textKey: 'hac-landing-gallery-text-CostaSmeralda',
      },
      {
        event: 'wimbledon2026',
        image: Wimbledon2026Gallery,
        titleKey: 'hac-landing-gallery-title-wimbledon2026',
        dateKey: '',
        textKey: 'hac-landing-gallery-text-wimbledon2026',
      },
      {
        event: 'almasixsenses',
        image: AlmasixsensesGallery,
        titleKey: 'hac-landing-gallery-title-almasixsenses',
        textKey: 'hac-landing-gallery-text-almasixsenses',
      },
      {
        event: 'alpine-escape',
        image: AlpineEscapeGallery,
        titleKey: 'hac-landing-gallery-title-alpine-escape',
        textKey: 'hac-landing-gallery-text-alpine-escape',
      },
      {
        event: 'hahnenkamm',
        image: HahnenkammGallery,
        titleKey: 'hac-landing-gallery-title-hahnenkamm',
        textKey: 'hac-landing-gallery-text-hahnenkamm',
      },
      {
        event: 'mykonos',
        image: MykonosGallery,
        titleKey: 'hac-landing-gallery-title-mykonos',
        textKey: 'hac-landing-gallery-text-mykonos',
      },
      {
        event: 'cotedAzur',
        image: cotedAzurGallery,
        titleKey: 'hac-landing-gallery-title-cotedAzur',
        textKey: 'hac-landing-gallery-text-cotedAzur',
      },
      {
        event: 'island-escape-mykonos',
        image: IslandEscapeMykonosGallery,
        titleKey: 'hac-landing-gallery-title-island-escape-mykonos',
        textKey: 'hac-landing-gallery-text-island-escape-mykonos',
      },
      {
        event: 'wimbledon',
        image: WimbledonGallery,
        titleKey: 'hac-landing-gallery-title-wimbledon',
        textKey: 'hac-landing-gallery-text-wimbledon',
      },
      {
        event: 'courmayeur',
        image: CourmayeurGallery,
        titleKey: 'hac-landing-gallery-title-courmayeur',
        textKey: 'hac-landing-gallery-text-courmayeur',
      },
      {
        event: 'champagne_new',
        image: ChampagneNewGallery,
        titleKey: 'hac-landing-gallery-title-champagne_new',
        textKey: 'hac-landing-gallery-text-champagne_new',
      },
      ...(canSeeAndorra
        ? [{
          event: 'andorra',
          image: AndorraGallery,
          titleKey: 'hac-landing-gallery-title-andorra',
          textKey: 'hac-landing-gallery-text-andorra',
        }]
        : []),
      ...(canSeeCourchevel
        ? [{
          event: 'courchevel',
          image: CourchevelGallery,
          titleKey: 'hac-landing-gallery-title-courchevel',
          textKey: 'hac-landing-gallery-text-courchevel',
        }]
        : []),
      {
        event: 'antwerp',
        image: AntwerpGallery,
        titleKey: 'hac-landing-gallery-title-antwerp',
        textKey: 'hac-landing-gallery-text-antwerp',
      },
      {
        event: 'hahnenkamm2026',
        image: Hahnenkamm2026Gallery,
        titleKey: 'hac-landing-gallery-title-hahnenkamm2026',
        dateKey: '',
        textKey: 'hac-landing-gallery-text-hahnenkamm2026',
      },
    ];

    return items;
  }, [canSeeAndorra, canSeeCourchevel]);

  const newExperiences = useMemo(() => [

    {
      event: 'kitzbuhel',
      image: KitzbuhelGallery,
      titleKey: 'hac-landing-gallery-title-kitzbuhel',
      dateKey: 'hac-landing-gallery-date-kitzbuhel',
      textKey: 'hac-landing-gallery-text-kitzbuhel',
    },
    {
      event: 'champagne',
      image: ChampagneGallery,
      titleKey: 'hac-landing-gallery-title-champagne',
      dateKey: 'hac-landing-gallery-date-champagne',
      textKey: 'hac-landing-gallery-text-champagne',
    },
/*    {
      event: 'PrivateParis',
      image: PrivateParisGallery,
      titleKey: 'hac-landing-gallery-title-PrivateParis',
      dateKey: 'hac-landing-gallery-date-PrivateParis',
      textKey: 'hac-landing-gallery-text-PrivateParis',
    },
    {
      event: 'CanMulo',
      image: CanMuloGallery,
      titleKey: 'hac-landing-gallery-title-CanMulo',
      dateKey: 'hac-landing-gallery-date-CanMulo',
      textKey: 'hac-landing-gallery-text-CanMulo',
    },
    {
      event: 'ibiza',
      image: IbizaGallery,
      titleKey: 'hac-landing-gallery-title-ibiza',
      dateKey: 'hac-landing-gallery-date-ibiza',
      textKey: 'hac-landing-gallery-text-ibiza',
    },
*/
    {
      event: 'culturalcurator',
      image: CulturalcuratorGallery,
      titleKey: 'hac-landing-gallery-title-culturalcurator',
      dateKey: 'hac-landing-gallery-date-culturalcurator',
      textKey: 'hac-landing-gallery-text-culturalcurator',
    },
    {
      event: 'inside-track',
      image: InsidetrackGallery,
      titleKey: 'hac-landing-gallery-title-inside-track',
      dateKey: 'hac-landing-gallery-date-inside-track',
      textKey: 'hac-landing-gallery-text-inside-track',
    },
    {
      event: 'eastnor',
      image: EastnorGallery,
      titleKey: 'hac-landing-gallery-title-eastnor',
      dateKey: 'hac-landing-gallery-date-eastnor',
      textKey: 'hac-landing-gallery-text-eastnor',
    },
    {
      event: 'caviarhouse',
      image: CaviarhouseGallery,
      titleKey: 'hac-landing-gallery-title-caviarhouse',
      dateKey: 'hac-landing-gallery-date-caviarhouse',
      textKey: 'hac-landing-gallery-text-caviarhouse',
    },
    {
      event: 'mayrlife',
      image: MayrlifeGallery,
      titleKey: 'hac-landing-gallery-title-mayrlife',
      dateKey: 'hac-landing-gallery-date-mayrlife',
      textKey: 'hac-landing-gallery-text-mayrlife',
    },
    {
      event: 'antoniewicz',
      image: AntoniewiczGallery,
      titleKey: 'hac-landing-gallery-title-antoniewicz',
      dateKey: 'hac-landing-gallery-date-antoniewicz',
      textKey: 'hac-landing-gallery-text-antoniewicz',
    },
    {
      event: 'hommage',
      image: HommageGallery,
      titleKey: 'hac-landing-gallery-title-hommage',
      dateKey: 'hac-landing-gallery-date-hommage',
      textKey: 'hac-landing-gallery-text-hommage',
    },
    {
      event: 'antinori',
      image: AntinoriGallery,
      titleKey: 'hac-landing-gallery-title-antinori',
      dateKey: 'hac-landing-gallery-date-antinori',
      textKey: 'hac-landing-gallery-text-antinori',
    },
    {
      event: 'ferretti',
      image: FerrettiGallery,
      titleKey: 'hac-landing-gallery-title-ferretti',
      dateKey: 'hac-landing-gallery-date-ferretti',
      textKey: 'hac-landing-gallery-text-ferretti',
    },
    {
      event: 'globeair',
      image: GlobeairGallery,
      titleKey: 'hac-landing-gallery-title-globeair',
      dateKey: 'hac-landing-gallery-date-globeair',
      textKey: 'hac-landing-gallery-text-globeair',
    },
    {
      event: 'mandarin',
      image: MandarinGallery,
      titleKey: 'hac-landing-gallery-title-mandarin',
      dateKey: 'hac-landing-gallery-date-mandarin',
      textKey: 'hac-landing-gallery-text-mandarin',
    },
    {
      event: 'mykonos2025',
      image: Mykonos2025Gallery,
      titleKey: 'hac-landing-gallery-title-mykonos2025',
      dateKey: 'hac-landing-gallery-date-mykonos2025',
      textKey: 'hac-landing-gallery-text-mykonos2025',
    },
    
  ], []);

  return (
    <>
      {role === 'hac' && (
        <div style={{ display: 'none' }}>
          <Wadsworth />
        </div>
      )}

      {token ? (
        <>
          <ExperienceCatalogHero />

          <div className="inner experience-catalog experience-catalog__content">
            <div className="tabs internal">
              <ul>
                <li className={activeTab === 'past' ? 'active' : ''}>
                  <span onClick={() => handleTabClick('past')}>{t('experiences-old')}</span>
                </li>
                <li className={activeTab === 'new' ? 'active' : ''}>
                  <span onClick={() => handleTabClick('new')}>{t('experiences-new')}</span>
                </li>
              </ul>
            </div>

            <h2>{t('hac-landing-gallery-title')}</h2>
            <p>{t('hac-landing-gallery-text')}</p>

            {activeTab === 'past' && (
              <div className="past-experiences">
                <ul>
                  {pastExperiences.map((item) => (
                    <ExperienceCatalogItem
                      key={item.event}
                      className={item.event ? `experience-catalog-item--${item.event}` : ''}
                      image={item.image}
                      titleKey={item.titleKey}
                      textKey={item.textKey}
                      dateKey={item.dateKey}
                      event={item.event}
                      linkRole={linkRole}
                    />
                  ))}
                </ul>

              </div>
            )}

            {activeTab === 'new' && (
              <div className="new-experiences">
                <ul>
                  {newExperiences.map((item) => (
                    <ExperienceCatalogItem
                      key={item.event}
                      className={item.event ? `experience-catalog-item--${item.event}` : ''}
                      image={item.image}
                      titleKey={item.titleKey}
                      textKey={item.textKey}
                      dateKey={item.dateKey}
                      event={item.event}
                      linkRole={linkRole}
                    />
                  ))}


                </ul>
              </div>
            )}
          </div>
        </>
      ) : (
        <Navigate to="/login" />
      )}
    </>
  );
};

export default ExperienceCatalog;
